import React, { useState, useEffect } from 'react';
import { Card, Tag, Typography, Space, Divider, Row, Col, Progress, Badge, Tooltip, Input } from 'antd';
import { DatabaseOutlined, ApartmentOutlined, ThunderboltOutlined, EditOutlined, CheckOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

const EditableRegisterName = ({ plcName, index, defaultName }) => {
  const storageKey = `plc_reg_name_${plcName}_${index}`;
  const [name, setName] = useState(() => localStorage.getItem(storageKey) || defaultName);
  const [isEditing, setIsEditing] = useState(false);
  const [tempName, setTempName] = useState(name);

  const saveName = () => {
    if (tempName.trim() === '') {
      setTempName(name); // Revert if empty
      setIsEditing(false);
      return;
    }
    setName(tempName);
    localStorage.setItem(storageKey, tempName);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Input 
        size="small" 
        value={tempName} 
        onChange={e => setTempName(e.target.value)}
        onPressEnter={saveName}
        onBlur={saveName}
        autoFocus
        style={{ fontSize: 10, marginBottom: 2 }}
        suffix={<CheckOutlined style={{ color: '#10b981', cursor: 'pointer' }} onClick={saveName} />}
      />
    );
  }

  return (
    <div 
      style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }} 
      onClick={() => setIsEditing(true)}
      title="Click para editar nombre"
    >
      <Text type="secondary" style={{ fontSize: 10, display: 'block' }} ellipsis>{name}</Text>
      <EditOutlined style={{ fontSize: 10, color: '#cbd5e1' }} />
    </div>
  );
};

const LedIndicator = ({ active, label, isOutput = false }) => {
  const color = isOutput ? (active ? '#f59e0b' : '#fef3c7') : (active ? '#10b981' : '#d1fae5');
  const shadow = active ? `0 0 8px ${color}` : 'none';
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <div 
        style={{ 
          width: 14, 
          height: 14, 
          borderRadius: '50%', 
          backgroundColor: color,
          boxShadow: shadow,
          border: `1px solid ${active ? 'transparent' : '#cbd5e1'}`,
          transition: 'all 0.2s ease'
        }} 
      />
      <Text style={{ fontSize: 9, color: '#64748b' }}>{label}</Text>
    </div>
  );
};

const PlcCard = ({ plc }) => {
  const isOnline = plc.status === 'online';
  // Fallbacks if backend hasn't sent the full object yet
  const registers = plc.data?.registers || plc.data || [0, 0, 0, 0];
  const inputs = plc.data?.inputs || [0, 0, 0, 0, 0, 0, 0, 0];
  const outputs = plc.data?.outputs || [0, 0, 0, 0, 0, 0, 0, 0];

  return (
    <Card 
      className="plc-card"
      style={{ 
        borderRadius: 12, 
        overflow: 'hidden', 
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
        background: '#f8fafc'
      }}
      bodyStyle={{ padding: 16 }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Space>
          <DatabaseOutlined style={{ fontSize: 20, color: '#64748b' }} />
          <div>
            <Title level={5} style={{ margin: 0 }}>{plc.name}</Title>
            <Text type="secondary" style={{ fontSize: 12 }}>{plc.ip}</Text>
          </div>
        </Space>
        <Badge status={isOnline ? 'processing' : 'default'} text={
          <Tag color={isOnline ? 'green' : 'red'}>{isOnline ? 'INDUSTRIAL OK' : 'OFFLINE'}</Tag>
        } />
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Registros D */}
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <Text strong style={{ fontSize: 12, color: '#475569' }}>
            <ApartmentOutlined /> REGISTROS MODBUS (HOLDING)
          </Text>
        </Col>
        
        {Array.isArray(registers) && registers.slice(0, 4).map((val, idx) => (
          <Col span={12} key={idx}>
            <div style={{ background: '#fff', padding: '8px 12px', borderRadius: 8, border: '1px solid #f1f5f9' }}>
              <EditableRegisterName plcName={plc.name} index={idx} defaultName={`REG ${idx}`} />
              <Title level={4} style={{ margin: 0, color: '#0f172a' }}>{val}</Title>
              <Progress 
                percent={Math.min(100, (val / 1000) * 100)} 
                showInfo={false} 
                size="small" 
                strokeColor={idx % 2 === 0 ? '#3b82f6' : '#10b981'}
              />
            </div>
          </Col>
        ))}
      </Row>

      {/* Panel de IO (Entradas y Salidas) */}
      <div style={{ background: '#fff', padding: 12, borderRadius: 8, border: '1px solid #f1f5f9', marginTop: 16 }}>
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <Text strong style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 8 }}>
              ENTRADAS DIGITALES (X)
            </Text>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 8px' }}>
              {Array.isArray(inputs) && inputs.slice(0, 8).map((val, idx) => (
                <LedIndicator key={`x${idx}`} active={val} label={`X${idx}`} isOutput={false} />
              ))}
            </div>
          </Col>
          <Col span={24}>
            <Text strong style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 8 }}>
              SALIDAS DIGITALES (Y)
            </Text>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 8px' }}>
              {Array.isArray(outputs) && outputs.slice(0, 8).map((val, idx) => (
                <LedIndicator key={`y${idx}`} active={val} label={`Y${idx}`} isOutput={true} />
              ))}
            </div>
          </Col>
        </Row>
      </div>

      <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        <ThunderboltOutlined style={{ color: '#eab308' }} />
        <Text type="secondary" style={{ fontSize: 11 }}>
          Última actualización: {plc.timestamp ? new Date(plc.timestamp).toLocaleTimeString() : 'N/A'}
        </Text>
      </div>
    </Card>
  );
};

export default PlcCard;
